import * as z from "zod";

import { amountSchema } from "./money";

export const paymentProviderTypeSchema = z
  .enum(["stripe"])
  .describe("PaymentProviderType");

// The mini-app that launched hosted onboarding. Stripe's return bounce lands
// the merchant back there, the surface that shows whether setup succeeded.
export const paymentsOnboardingSurfaceSchema = z
  .enum(["settings", "payments"])
  .describe("PaymentsOnboardingSurface");

export type PaymentsOnboardingSurface = z.infer<
  typeof paymentsOnboardingSurfaceSchema
>;

// Whether the account can calculate automatic tax at checkout; the two
// requires* members name which setup step is missing.
export const automaticTaxStatusSchema = z
  .enum(["available", "requiresPaymentAccount", "requiresTaxSetup"])
  .describe("AutomaticTaxStatus");

export type AutomaticTaxStatus = z.infer<typeof automaticTaxStatusSchema>;

/**
 * Where a project stands on being able to charge shoppers, as one total answer
 * so every surface switches once instead of re-walking the account fields.
 * `inReview` and `rejected` are the two the merchant cannot act on: the
 * provider is verifying details it already has, or it has declined the account.
 */
export const paymentAccountStatusSchema = z
  .enum([
    "notConnected",
    "onboardingIncomplete",
    "inReview",
    "actionRequired",
    "rejected",
    "ready",
  ])
  .describe("PaymentAccountStatus");

export type PaymentAccountStatus = z.infer<typeof paymentAccountStatusSchema>;

/** The subset that describes an account whose KYC is in, but cannot charge. */
export type BlockedPaymentAccountStatus = Extract<
  PaymentAccountStatus,
  "inReview" | "actionRequired" | "rejected"
>;

/**
 * Wire projection of a payment provider account row, derived from its
 * lifecycle timestamps by `toPaymentProviderAccount`. Provider-neutral: the
 * account id keeps the row's `externalAccountId` name, and capabilities use
 * the platform-industry names (Adyen: receivePayments/payouts) rather than
 * Stripe's charges_enabled/payouts_enabled, which stay confined to the
 * webhook-snapshot adapter boundary.
 */
export const paymentProviderAccountSchema = z
  .object({
    externalAccountId: z.string(),
    detailsSubmitted: z.boolean(),
    capabilities: z.object({
      receivePayments: z.boolean(),
      payouts: z.boolean(),
    }),
    canAcceptPayments: z.boolean(),
  })
  .describe("PaymentProviderAccount");

type PaymentProviderAccount = z.infer<typeof paymentProviderAccountSchema>;

// NOTE (Max, 2026-07-22): Prisma rows carry Date timestamps; ark's Drizzle
// rows carry ISO strings. Both orderings agree — normalize through epoch millis.
type TimestampValue = Date | string;

/**
 * Structural row shape shared by the Prisma model and the ark Drizzle table,
 * so every runtime derives the wire account through this one module.
 */
export type PaymentProviderAccountTimestamps = {
  externalAccountId: string;
  detailsSubmittedAt: TimestampValue | null;
  chargesEnabledAt: TimestampValue | null;
  chargesDisabledAt: TimestampValue | null;
  payoutsEnabledAt: TimestampValue | null;
  payoutsDisabledAt: TimestampValue | null;
};

export function isCurrentlyEnabled({
  enabledAt,
  disabledAt,
}: {
  enabledAt: TimestampValue | null;
  disabledAt: TimestampValue | null;
}): boolean {
  if (!enabledAt) {
    return false;
  }
  if (!disabledAt) {
    return true;
  }
  return new Date(enabledAt).getTime() > new Date(disabledAt).getTime();
}

export function toPaymentProviderAccount(
  row: PaymentProviderAccountTimestamps,
): PaymentProviderAccount {
  const receivePayments = isCurrentlyEnabled({
    enabledAt: row.chargesEnabledAt,
    disabledAt: row.chargesDisabledAt,
  });
  return {
    // The account's id in the provider's own system (Stripe: "acct_…").
    externalAccountId: row.externalAccountId,
    // Onboarding paperwork (KYC) done. Monotonic — details never un-submit.
    detailsSubmitted: Boolean(row.detailsSubmittedAt),
    capabilities: {
      // Provider currently allows charging shoppers. Revocable at any time
      // (requirements past due, risk review), hence derived latest-wins.
      receivePayments,
      // Provider currently allows paying the merchant out. Independent of
      // receivePayments — a dead bank account kills payouts while sales continue.
      payouts: isCurrentlyEnabled({
        enabledAt: row.payoutsEnabledAt,
        disabledAt: row.payoutsDisabledAt,
      }),
    },
    // The gate every consumer asks about: is checkout usable right now.
    // Defined, not stored — a stored flag could contradict its inputs.
    canAcceptPayments: Boolean(row.detailsSubmittedAt) && receivePayments,
  };
}

export const paymentsBankAccountSchema = z
  .object({
    // Null when the provider does not name the bank (some non-US accounts).
    bankName: z.string().nullable(),
    last4: z.string(),
  })
  .describe("PaymentsBankAccount");

export const paymentsPayoutStatusSchema = z
  .enum(["pending", "inTransit", "paid", "failed", "canceled"])
  .describe("PaymentsPayoutStatus");

export const paymentsPayoutSchema = z
  .object({
    id: z.string(),
    amount: amountSchema,
    currencyCode: z.string(),
    status: paymentsPayoutStatusSchema,
    // The day the bank is expected to have, or did receive, the money.
    arrivalDate: z.iso.date(),
    bankAccount: paymentsBankAccountSchema.nullable(),
  })
  .describe("PaymentsPayout");

/** Where unpaid money is: `clearing` (not yet released), `ready` (awaiting the next payout), `sent` (on its way). */
export const paymentsNextPayoutSchema = z
  .object({
    stage: z.enum(["clearing", "ready", "sent"]),
    amount: amountSchema,
    currencyCode: z.string(),
    // Known only once the provider has created the payout.
    arrivalDate: z.iso.date().nullable(),
    payoutId: z.string().nullable(),
  })
  .describe("PaymentsNextPayout");

// What the provider still needs, in merchant terms rather than its requirement keys.
export const paymentsAccountNeedSchema = z
  .enum([
    "identityDocument",
    "bankAccount",
    "taxId",
    "termsAcceptance",
    "businessDetails",
  ])
  .describe("PaymentsAccountNeed");

const payoutWeekdaySchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

/** `manual` (sent only on request) is never set by this product, but an account can arrive with it. */
export const paymentsPayoutScheduleSchema = z
  .discriminatedUnion("interval", [
    z.object({ interval: z.literal("daily") }),
    z.object({ interval: z.literal("weekly"), weekday: payoutWeekdaySchema }),
    z.object({
      interval: z.literal("monthly"),
      // 31 means the last day of shorter months.
      dayOfMonth: z.number().int().min(1).max(31),
    }),
    z.object({ interval: z.literal("manual") }),
  ])
  .describe("PaymentsPayoutSchedule");

// The schedules a merchant can choose; `manual` is read-only.
export const paymentsPayoutScheduleInputSchema = z
  .discriminatedUnion("interval", [
    z.object({ interval: z.literal("daily") }),
    z.object({ interval: z.literal("weekly"), weekday: payoutWeekdaySchema }),
    z.object({
      interval: z.literal("monthly"),
      dayOfMonth: z.number().int().min(1).max(31),
    }),
  ])
  .describe("PaymentsPayoutScheduleInput");

export const paymentsSummarySchema = z
  .object({
    status: paymentAccountStatusSchema,
    // Payouts are held independently of charging: a dead bank account stops payouts while sales continue.
    payoutsPaused: z.boolean(),
    needs: z.array(paymentsAccountNeedSchema),
    // Null until the account exists at the provider.
    balance: z
      .object({
        available: amountSchema,
        pending: amountSchema,
        currencyCode: z.string(),
      })
      .nullable(),
    nextPayout: paymentsNextPayoutSchema.nullable(),
    payouts: z.array(paymentsPayoutSchema),
    bankAccount: paymentsBankAccountSchema.nullable(),
    payoutSchedule: paymentsPayoutScheduleSchema.nullable(),
    // Null when sold orders span currencies: a cross-currency sum has no honest single number.
    sales: z
      .object({
        netAmount: amountSchema,
        currencyCode: z.string(),
        orderCount: z.number().int().nonnegative(),
        // One entry per UTC day, oldest first, zero-filled.
        days: z.array(
          z.object({ date: z.iso.date(), netAmount: amountSchema }),
        ),
      })
      .nullable(),
  })
  .describe("PaymentsSummary");

export const paymentsPayoutDetailSchema = z
  .object({
    payout: paymentsPayoutSchema,
    // Refunds and fees are positive amounts taken out of `grossSales`.
    grossSales: amountSchema,
    refunds: amountSchema,
    fees: amountSchema,
    // Everything else settled (dispute holds, adjustments), signed, so the lines add up to the payout.
    otherAdjustments: amountSchema,
    saleCount: z.number().int().nonnegative(),
  })
  .describe("PaymentsPayoutDetail");

export const paymentsDisputeStatusSchema = z
  .enum(["needsResponse", "underReview", "won", "lost"])
  .describe("PaymentsDisputeStatus");

export const paymentsDisputeSchema = z
  .object({
    id: z.string(),
    amount: amountSchema,
    currencyCode: z.string(),
    // The card network's reason, e.g. "fraudulent" or "product_not_received".
    reason: z.string(),
    status: paymentsDisputeStatusSchema,
    // Deadline to submit evidence; null once the dispute no longer takes any.
    respondBy: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .describe("PaymentsDispute");
